 import { extendTheme } from '@chakra-ui/react';
import { format, parseISO, formatDistanceToNow, isValid } from 'date-fns';

/**
 * Chakra UI Theme Configuration
 * Provides consistent styling across the application
 */
export const theme = extendTheme({
    config: {
        initialColorMode: 'light',
        useSystemColorMode: false,
    },
    colors: {
        brand: {
            50: '#e6f3ff',
            100: '#bae1ff',
            200: '#8cceff',
            300: '#5ebaff',
            400: '#30a6ff',
            500: '#4318ff',
            600: '#3614cc',
            700: '#2a1099',
            800: '#1d0c66',
            900: '#110833',
        },
        gray: {
            50: '#f7fafc',
            100: '#edf2f7',
            200: '#e2e8f0',
            300: '#cbd5e0',
            400: '#a0aec0',
            500: '#718096',
            600: '#4a5568',
            700: '#2d3748',
            800: '#1a202c',
            900: '#171923',
        },
    },
    fonts: {
        heading: 'DM Sans, sans-serif',
        body: 'DM Sans, sans-serif',
    },
    components: {
        Button: {
            baseStyle: {
                fontWeight: '500',
                borderRadius: '12px',
            },
            variants: {
                solid: {
                    bg: 'brand.500',
                    color: 'white',
                    _hover: {
                        bg: 'brand.600',
                    },
                },
                outline: {
                    borderColor: 'brand.500',
                    color: 'brand.500',
                    _hover: {
                        bg: 'brand.50',
                    },
                },
            },
        },
        Card: {
            baseStyle: {
                container: {
                    borderRadius: '20px',
                    boxShadow: '0px 18px 40px rgba(112, 144, 176, 0.12)',
                    border: '1px solid',
                    borderColor: 'gray.200',
                    _dark: {
                        borderColor: 'gray.700',
                        boxShadow: '0px 18px 40px rgba(4, 7, 18, 0.12)',
                    },
                },
            },
        },
        Table: {
            variants: {
                simple: {
                    th: {
                        borderColor: 'gray.200',
                        _dark: {
                            borderColor: 'gray.700',
                        },
                    },
                    td: {
                        borderColor: 'gray.200',
                        _dark: {
                            borderColor: 'gray.700',
                        },
                    },
                },
            },
        },
    },
    styles: {
        global: (props) => ({
            body: {
                bg: props.colorMode === 'dark' ? 'gray.900' : 'gray.50',
                color: props.colorMode === 'dark' ? 'gray.100' : 'gray.800',
            },
        }),
    },
});

/**
 * Date and Time Formatting Utilities
 * Handles ISO 8601 datetime parsing and local timezone conversion
 * Supports both UTC and local time display as per requirements
 */

/**
 * Parse ISO 8601 datetime string to Date object
 * Handles the YYYY-MM-DDTHH:MM:SS.sssZ format from CAS backend
 */
export const parseDateTime = (dateString) => {
    if (!dateString) return null;
    
    try {
        // Handle ISO 8601 format from backend
        if (typeof dateString === 'string') {
            return parseISO(dateString);
        }
        
        // Handle Date objects
        if (dateString instanceof Date) {
            return dateString;
        }
        
        return null;
    } catch (error) {
        console.error('Date parsing error:', error, 'Input:', dateString);
        return null;
    }
};

/**
 * Format datetime for display in local timezone
 * Converts UTC datetime to local display format (24-hour)
 */
export const formatDateTime = (date, options = {}) => {
    const parsedDate = parseDateTime(date);
    if (!parsedDate || !isValid(parsedDate)) {
        return 'Invalid Date';
    }
    
    const defaultOptions = {
        showSeconds: false,
        showDate: true,
        showTime: true,
        format24Hour: true,
        timeZone: 'local' // 'local' or 'utc'
    };
    
    const config = { ...defaultOptions, ...options };
    
    try {
        let formatString = '';
        
        if (config.showDate && config.showTime) {
            formatString = config.showSeconds ? 
                'dd/MM/yyyy HH:mm:ss' : 
                'dd/MM/yyyy HH:mm';
        } else if (config.showDate) {
            formatString = 'dd/MM/yyyy';
        } else if (config.showTime) {
            formatString = config.showSeconds ? 'HH:mm:ss' : 'HH:mm';
        }
        
        return format(parsedDate, formatString);
    } catch (error) {
        console.error('Date formatting error:', error);
        return 'Format Error';
    }
};

/**
 * Format datetime for display with timezone information
 * Shows both local and UTC time when needed
 */
export const formatDateTimeWithTZ = (date, showUTC = false) => {
    const parsedDate = parseDateTime(date);
    if (!parsedDate || !isValid(parsedDate)) {
        return 'Invalid Date';
    }
    
    const localTime = formatDateTime(parsedDate);
    
    if (!showUTC) {
        return `${localTime} (Local)`;
    }
    
    const utcTime = format(parsedDate, 'dd/MM/yyyy HH:mm');
    return `${localTime} (Local) | ${utcTime} (UTC)`;
};

/**
 * Get relative time string (e.g., "2 hours ago")
 * Used for notification timestamps and activity logs
 */
export const getRelativeTime = (date) => {
    const parsedDate = parseDateTime(date);
    if (!parsedDate || !isValid(parsedDate)) {
        return 'Unknown time';
    }
    
    try {
        return formatDistanceToNow(parsedDate, { addSuffix: true });
    } catch (error) {
        console.error('Relative time formatting error:', error);
        return 'Unknown time';
    }
};

/**
 * Data Formatting Utilities
 * Helper functions for consistent data display across components
 */

/**
 * Format numbers with proper thousand separators
 * Handles large numbers and maintains readability
 */
export const formatNumber = (num, options = {}) => {
    if (num === null || num === undefined || isNaN(num)) {
        return '0';
    }
    
    const defaultOptions = {
        decimals: 0,
        thousandsSeparator: ',',
        prefix: '',
        suffix: ''
    };
    
    const config = { ...defaultOptions, ...options };
    
    try {
        const formatted = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: config.decimals,
            maximumFractionDigits: config.decimals
        }).format(num);
        
        return `${config.prefix}${formatted}${config.suffix}`;
    } catch (error) {
        console.error('Number formatting error:', error);
        return String(num);
    }
};

/**
 * Format percentage values
 * Ensures consistent percentage display with proper rounding
 */
export const formatPercentage = (value, decimals = 1) => {
    if (value === null || value === undefined || isNaN(value)) {
        return '0%';
    }
    
    return `${Number(value).toFixed(decimals)}%`;
};

/**
 * Format file sizes in human-readable format
 * Converts bytes to appropriate units (KB, MB, GB)
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    if (!bytes || isNaN(bytes)) return 'Unknown size';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Text and String Utilities
 * Helper functions for text processing and display
 */

/**
 * Truncate text with ellipsis
 * Ensures consistent text overflow handling
 */
export const truncateText = (text, maxLength = 50) => {
    if (!text || typeof text !== 'string') return '';
    if (text.length <= maxLength) return text;
    
    return text.substring(0, maxLength).trim() + '...';
};

/**
 * Capitalize first letter of each word
 * Provides consistent title case formatting
 */
export const toTitleCase = (str) => {
    if (!str || typeof str !== 'string') return '';
    
    return str.replace(/\w\S*/g, (txt) => 
        txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
};

/**
 * Generate initials from name
 * Used for avatar fallbacks when profile images unavailable
 */
export const getInitials = (name) => {
    if (!name || typeof name !== 'string') return 'U';
    
    const names = name.trim().split(' ');
    if (names.length === 1) {
        return names[0].charAt(0).toUpperCase();
    }
    
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

/**
 * Status and State Utilities
 * Helper functions for status indicators and state management
 */

/**
 * Get status color based on status value
 * Provides consistent color coding across status indicators
 */
export const getStatusColor = (status) => {
    const statusColors = {
        // Execution statuses
        'success': 'green',
        'error': 'red',
        'pending': 'yellow',
        'running': 'blue',
        'failed': 'red',
        
        // Client statuses
        'online': 'green',
        'offline': 'red',
        'maintenance': 'orange',
        
        // User statuses
        'approved': 'green',
        'rejected': 'red',
        'active': 'green',
        'inactive': 'gray',
        
        // Report statuses
        'enabled': 'green',
        'disabled': 'gray',
        
        // Default
        'default': 'gray'
    };
    
    return statusColors[status?.toLowerCase()] || statusColors.default;
};

/**
 * Get status display text
 * Provides user-friendly status text with proper formatting
 */
export const getStatusText = (status) => {
    if (!status) return 'Unknown';
    
    return toTitleCase(status.replace(/[_-]/g, ' '));
};

/**
 * Validation Utilities
 * Helper functions for form validation and data checking
 */

/**
 * Validate email format
 * Basic email validation for form inputs
 */
export const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

/**
 * Validate URL format
 * Basic URL validation for configuration inputs
 */
export const isValidUrl = (url) => {
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
};

/**
 * Check if value is empty or null
 * Unified empty value checking across components
 */
export const isEmpty = (value) => {
    return value === null || 
           value === undefined || 
           value === '' || 
           (Array.isArray(value) && value.length === 0) ||
           (typeof value === 'object' && Object.keys(value).length === 0);
};

/**
 * API and Data Utilities
 * Helper functions for API data processing
 */

/**
 * Extract error message from API response
 * Provides consistent error message extraction
 */
export const getErrorMessage = (error) => {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.response?.statusText) return error.response.statusText;
    
    return 'An unexpected error occurred';
};

/**
 * Generate unique ID for components
 * Provides unique identifiers for dynamic components
 */
export const generateId = (prefix = 'id') => {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Deep clone object
 * Provides safe object cloning for state management
 */
export const deepClone = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj;
    if (obj instanceof Date) return new Date(obj.getTime());
    if (obj instanceof Array) return obj.map(item => deepClone(item));
    if (typeof obj === 'object') {
        const cloned = {};
        Object.keys(obj).forEach(key => {
            cloned[key] = deepClone(obj[key]);
        });
        return cloned;
    }
};

/**
 * Configuration and Environment Utilities
 * Helper functions for application configuration
 */

/**
 * Get configuration value with fallback
 * Safely access nested configuration properties
 */
export const getConfig = (path, defaultValue = null) => {
    const config = window.ADI_CONFIG;
    if (!config) return defaultValue;
    
    const keys = path.split('.');
    let current = config;
    
    for (const key of keys) {
        if (current[key] === undefined) {
            return defaultValue;
        }
        current = current[key];
    }
    
    return current;
};

/**
 * Check if application is in development mode
 * Determines environment for debugging features
 */
export const isDevelopment = () => {
    return process.env.NODE_ENV === 'development' || 
           getConfig('app.environment') === 'development';
};

/**
 * Logging utility with environment awareness
 * Provides consistent logging across the application
 */
export const log = {
    info: (message, ...args) => {
        if (isDevelopment() || getConfig('logging.enableConsole', true)) {
            console.log(`[INFO] ${message}`, ...args);
        }
    },
    warn: (message, ...args) => {
        if (isDevelopment() || getConfig('logging.enableConsole', true)) {
            console.warn(`[WARN] ${message}`, ...args);
        }
    },
    error: (message, ...args) => {
        if (isDevelopment() || getConfig('logging.enableConsole', true)) {
            console.error(`[ERROR] ${message}`, ...args);
        }
    },
    debug: (message, ...args) => {
        if (isDevelopment()) {
            console.debug(`[DEBUG] ${message}`, ...args);
        }
    }
};


export const calculateDuration = (offlineTime, onlineTime) => {
    if (!offlineTime || !onlineTime) return '-';
    
    const offline = new Date(offlineTime);
    const online = new Date(onlineTime);
    const diffMs = online - offline;
    
    if (diffMs < 0) return 'Invalid';
    
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };
